import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAdminAuth } from '../context/AdminAuthContext';
import TopBar from '../components/TopBar';
import PageTransition from '../components/ui/PageTransition';
import Button from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import { AnimatePresence } from 'framer-motion';
import './AdminLogin.css';
import { AxiosError } from 'axios';

interface ApiErrorResponse {
  message?: string;
  error?: string;
}

const AdminLogin: React.FC = () => {
  const [cpf, setCpf] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setAdminAuth } = useAdminAuth();

  const formatCPF = (value: string): string => {
    const cleanedValue = value.replace(/\D/g, '');
    if (cleanedValue.length > 11) return cleanedValue.slice(0, 11);
    return cleanedValue
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
  };

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsLoading(true);

    const cleanCpf = cpf.replace(/\D/g, '');
    if (cleanCpf.length !== 11) {
      setErrorMessage('CPF deve conter 11 dígitos.');
      setIsLoading(false);
      return;
    }

    if (!password) {
      setErrorMessage('Senha é obrigatória.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await api.post('/auth/login', {
        cpf: cleanCpf,
        password,
      });

      // Resposta esperada:
      // {
      //   "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
      //   "user": {
      //     "id": 4,
      //     "cpf": "12345678971",
      //     "user_level": "admin" or "super_admin"
      //   }
      // }
      const { token, user } = response.data;

      if (user.user_level !== 'admin' && user.user_level !== 'super_admin') {
        setErrorMessage('Acesso restrito a administradores.');
        setIsLoading(false);
        return;
      }

      setAdminAuth(user.id.toString(), token, user.user_level);
      navigate('/rj/admin/dashboard');
    } catch (err) {
      const error = err as AxiosError<ApiErrorResponse>;
      console.error('Erro ao fazer login de administrador:', error.response?.status, error.response?.data);
      setErrorMessage(error.response?.data?.message || 'Falha no login de administrador. Verifique suas credenciais.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <TopBar />
      <PageTransition>
        <div className="admin-login-wrapper">
          <form className="admin-login-form" onSubmit={handleAdminLogin}>
            <span className="admin-login-badge" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2 4 6v6c0 4.4 3.4 8.5 8 10 4.6-1.5 8-5.6 8-10V6l-8-4z" />
                <path d="M9.5 12.5 11.3 14.3 15 10.6" />
              </svg>
            </span>
            <h2>Login Administrador</h2>

            <AnimatePresence>
              {errorMessage && <Alert variant="error">{errorMessage}</Alert>}
            </AnimatePresence>

            <div className="form-group">
              <label htmlFor="cpf">CPF</label>
              <input
                id="cpf"
                type="text"
                value={cpf}
                onChange={(e) => setCpf(formatCPF(e.target.value))}
                placeholder="000.000.000-00"
                className="form-input"
                maxLength={14}
                required
                disabled={isLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Senha</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="form-input"
                required
                disabled={isLoading}
              />
            </div>

            <Button type="submit" loading={isLoading} disabled={isLoading} className="admin-login-submit" aria-label="Entrar como administrador">
              {isLoading ? 'Entrando...' : 'Entrar como Admin'}
            </Button>
          </form>
        </div>
      </PageTransition>
    </>
  );
};

export default AdminLogin;
